import { addTypenameToDocument } from "@apollo/client/utilities";
import { GraphQLError, parse } from "graphql";
import { request } from "graphql-request";

import appConfig from "config/app";
import { useAuthStore } from "src/stores/auth-store";
import { logger, maskSensitiveData, stringify } from "src/utils";

interface ErrorResponse {
  message: string;
  path: Array<string>;
  extensions: {
    [key: string]: any;
  };
}

export interface ServerError {
  response?: {
    errors?: Array<GraphQLError>;
  };
  request?: {
    query?: string;
    variables?: {
      [key: string]: any;
    };
  };
}

interface Error {
  response: {
    errors: Array<ErrorResponse>;
  };
  request: {
    [key: string]: any;
  };
}

const handleErrors = (errors: Array<ErrorResponse>) => {
  errors.forEach(async (error) => {
    const { message, path, extensions } = error;

    logger.error(
      `[GraphQL error]: Message: ${message}, Path: ${path}, Extensions: ${stringify(extensions)}`
    );
  });
};

export const fetcher = <TData, TVariables extends Record<string, any>>(
  query: string,
  variables?: TVariables,
  // eslint-disable-next-line no-undef
  options?: RequestInit["headers"]
): (() => Promise<TData>) => {
  return async () => {
    const { token } = useAuthStore.getState();

    const requestHeaders = {
      ...options,
      authorization: token ? `Bearer ${token}` : "",
    };

    // Adds "__typename" to the query
    const document = addTypenameToDocument(parse(query));

    try {
      logger.info(
        `query ${query} start with variables: ${JSON.stringify(maskSensitiveData(variables))}`
      );

      if (appConfig.graphql.urlNext) {
        const data = await request(
          `${appConfig.graphql.urlNext}/graphql`,
          document,
          variables,
          requestHeaders
        );

        logger.info(`query ${query} end with: ${JSON.stringify(data)}`);

        return data;
      }

      return null;
    } catch (err) {
      const error = err as Error;
      if (error?.response?.errors) {
        handleErrors(error.response.errors);
      }

      throw error;
    }
  };
};
